/**
 * The three instruments the dashboard draws with — ARCHITECTURE §16.7.
 *
 * One bar, one strip, one ring, defined once, so the stat band, the estate
 * panel and the activity feed cannot disagree about how heavy a mark is or what
 * a track is made of. Every one of them is `aria-hidden`: **not a single value
 * on this screen is carried by a graphic alone.** The number a mark encodes is
 * always text in the DOM beside it, which is what makes it safe to draw with a
 * palette whose `attention` and `stopped` measure ΔE 6.1 apart (§16.7, and the
 * argument `SimStatusChart`'s header has always made).
 *
 * What is deliberately NOT here: sparklines, trend lines, deltas, arrows.
 * `AccountStats` is a snapshot with no history behind it, and a trend drawn
 * from one reading is a confident picture of a fact we do not have — the same
 * defect `usageMeter()` returns `null` to avoid.
 */

import { useId, type ReactElement } from 'react';

import { TONE_FILL, TONE_BG, TONE_STROKE, type ChartTone } from './tones.js';

/* ------------------------------------------------------------------ *
 * Bar — a single proportion. Two divs; a rectangle does not need a
 * drawing surface.
 * ------------------------------------------------------------------ */

/**
 * The `ring-1 ring-inset ring-line` on the track is the part that matters
 * rather than the part that decorates. `--color-chart-track` is a platform
 * grey, but the *surface* it sits on is tenant ink: on a tenant whose surface
 * is already near that grey the track vanishes, and with it the 100% extent the
 * reader measures the fill against. The hairline survives that. The track is
 * not decoration — it is the denominator.
 */
export function Bar({
  share,
  tone,
  className = '',
}: {
  /** 0–100. Clamped rather than trusted: a bar wider than its track is a lie. */
  share: number;
  tone: ChartTone;
  className?: string;
}): ReactElement {
  const width = Math.min(100, Math.max(0, share));
  return (
    <div
      aria-hidden="true"
      className={`h-[var(--chart-track-h)] w-full overflow-hidden rounded-[var(--chart-cap)] bg-chart-track ring-1 ring-inset ring-line ${className}`}
    >
      <div
        // `--motion-base` is 0ms under `prefers-reduced-motion` (globals), so
        // the grow-in is opt-out by construction rather than by a decision
        // taken here.
        className={`h-full rounded-[var(--chart-cap)] transition-[width] duration-[var(--motion-base)] ease-[var(--ease-out)] ${TONE_BG[tone]}`}
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Strip — the stacked composition.
 * ------------------------------------------------------------------ */

export interface StripSlice {
  /** Only ever a React key here; the label a person reads is in the list. */
  label: string;
  tone: ChartTone;
  /** 0–100, of the same total as every other slice in the strip. */
  share: number;
}

/**
 * Period of the hatch, in user units. A `<pattern>`'s `width`/`height` are
 * presentation attributes and **cannot take a `var()`**, which is why this is a
 * module constant and not a token.
 */
const HATCH_PERIOD = 5;
const HATCH_STROKE = 1.5;

const HEIGHT_CLASS: Record<'sm' | 'md', string> = {
  /** Under a stat numeral, where the strip is a footnote to the figure. */
  sm: 'h-[6px]',
  /** The default: thicker than a bar because it carries interior joins. */
  md: 'h-[var(--chart-stack-h)]',
};

/**
 * The composition strip.
 *
 * **The geometry rule: no `viewBox` and no `preserveAspectRatio`.** Segments are
 * percentage-width `<rect>`s, and SVG resolves percentage lengths against the
 * element's own viewport — so nothing is ever non-uniformly scaled. That is what
 * keeps a 2px gap exactly 2px and a 45° hatch exactly 45°. The obvious first
 * attempt (`viewBox="0 0 100 12"` with `preserveAspectRatio="none"`) stretches
 * horizontally and smears the hatch into a shallow gradient.
 *
 * The rounded outer ends come from the HTML wrapper, so the radius applies in
 * CSS pixels and the interior joins stay square.
 *
 * **Texture is assigned by ordinal position among same-tone slices — first
 * solid, second 45°, third 135° — not by status identity.** Its job is
 * separation, not identity: safe custody, suspended and temporarily
 * disconnected all wear the same amber, and three adjacent amber segments with
 * only a hairline between them read as one segment. Identity stays with the
 * labelled list beside it.
 *
 * Short slices are **not** floored to a visible minimum, unlike the row bars in
 * `SimStatusChart`. A floor there lengthens one independent bar; a floor here
 * would push every segment after it sideways, so the strip would stop being a
 * true partition of the total it claims to divide.
 */
export function Strip({
  slices,
  height = 'md',
  className = '',
}: {
  slices: readonly StripSlice[];
  height?: 'sm' | 'md';
  className?: string;
}): ReactElement | null {
  /*
    `<pattern>` ids are document-global and the dashboard renders three strips.
    A collision silently gives the second strip the first strip's fill, which is
    invisible until two charts disagree in front of an operator. `useId()` is
    stripped to alphanumerics because React 19 spells its ids with punctuation
    that is legal in an id but awkward in a `url(#…)` reference.
  */
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const hatchIds: readonly (string | null)[] = [null, `strip-diag-a-${uid}`, `strip-diag-b-${uid}`];

  if (slices.length === 0) return null;

  const seen: Partial<Record<ChartTone, number>> = {};
  let offset = 0;
  const segments = slices.map((slice) => {
    const width = Math.min(100 - offset, Math.max(0, slice.share));
    const ordinal = seen[slice.tone] ?? 0;
    seen[slice.tone] = ordinal + 1;
    const segment = {
      key: slice.label,
      tone: slice.tone,
      x: offset,
      width,
      hatch: hatchIds[ordinal % hatchIds.length] ?? null,
    };
    offset += width;
    return segment;
  });

  return (
    <div
      aria-hidden="true"
      className={`w-full overflow-hidden rounded-[var(--chart-cap)] bg-chart-track ${HEIGHT_CLASS[height]} ${className}`}
    >
      <svg width="100%" height="100%" focusable="false" className="block h-full w-full">
        <defs>
          {/* Diagonals only. A horizontal or vertical hatch reads as gridlines,
              which are a scale — a thing this strip does not have. */}
          <pattern
            id={`strip-diag-a-${uid}`}
            width={HATCH_PERIOD}
            height={HATCH_PERIOD}
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <line
              x1="0"
              y1="0"
              x2="0"
              y2={HATCH_PERIOD}
              strokeWidth={HATCH_STROKE}
              // The hatch is drawn in the surface colour so it reads as an
              // extension of the gap between segments rather than as a second
              // colour nobody measured.
              className="stroke-surface"
            />
          </pattern>
          <pattern
            id={`strip-diag-b-${uid}`}
            width={HATCH_PERIOD}
            height={HATCH_PERIOD}
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(135)"
          >
            <line
              x1="0"
              y1="0"
              x2="0"
              y2={HATCH_PERIOD}
              strokeWidth={HATCH_STROKE}
              className="stroke-surface"
            />
          </pattern>
        </defs>

        {segments.map((segment) => (
          <g key={segment.key}>
            {/*
              The join is a STROKE in the surface colour straddling the shared
              edge, not a border around the mark: a border adds ink that is not
              data and makes every segment read as slightly larger than its
              value. `crispEdges` keeps the hairline from blurring across a
              fractional device pixel.
            */}
            <rect
              x={`${segment.x}%`}
              y="0"
              width={`${segment.width}%`}
              height="100%"
              shapeRendering="crispEdges"
              className={`${TONE_FILL[segment.tone]} stroke-surface [stroke-width:var(--chart-gap)]`}
            />
            {segment.hatch ? (
              <rect
                x={`${segment.x}%`}
                y="0"
                width={`${segment.width}%`}
                height="100%"
                fill={`url(#${segment.hatch})`}
              />
            ) : null}
          </g>
        ))}
      </svg>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Ring — one proportion against its own total.
 * ------------------------------------------------------------------ */

/**
 * The viewBox is sized to the rendered pixel box on purpose: one user unit is
 * one CSS pixel, so `--chart-ring-sw` means the 8px it says rather than 8px
 * scaled by whatever ratio a 0–100 viewBox happened to introduce.
 */
const RING: Record<'sm' | 'md', { box: number; r: number; strokeClass: string; sizeClass: string }> = {
  sm: {
    box: 28,
    r: 11,
    strokeClass: '[stroke-width:calc(var(--chart-ring-sw)_/_2)]',
    sizeClass: 'size-[28px]',
  },
  md: {
    box: 72,
    r: 30,
    strokeClass: '[stroke-width:var(--chart-ring-sw)]',
    sizeClass: 'size-[72px]',
  },
};

/**
 * **Always a single proportion — one value against its own total — and always a
 * chart tone, never the brand** (§16.7 ruling A).
 *
 * This is deliberately not a donut. `SimStatusChart`'s header is right that
 * reading a share off an arc is guesswork past two or three slices, and our
 * statuses run to eight; the multi-category answer is the labelled bar list.
 * One arc against its own remainder is the case an arc is actually good at:
 * "most of the estate" versus "about half" at a glance.
 */
export function Ring({
  share,
  tone,
  size = 'md',
  className = '',
}: {
  /** 0–100. */
  share: number;
  tone: ChartTone;
  size?: 'sm' | 'md';
  className?: string;
}): ReactElement {
  const { box, r, strokeClass, sizeClass } = RING[size];
  const centre = box / 2;
  const circumference = 2 * Math.PI * r;
  const fraction = Math.min(1, Math.max(0, share / 100));

  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox={`0 0 ${box} ${box}`}
      className={`${sizeClass} ${className}`}
    >
      <circle
        cx={centre}
        cy={centre}
        r={r}
        className={`fill-none stroke-chart-track ${strokeClass}`}
      />
      <circle
        cx={centre}
        cy={centre}
        r={r}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={circumference * (1 - fraction)}
        // Twelve o'clock is where a reader expects an arc to start; the default
        // is three o'clock, which reads as an arbitrary offset.
        transform={`rotate(-90 ${centre} ${centre})`}
        className={`fill-none ${TONE_STROKE[tone]} ${strokeClass}`}
      />
    </svg>
  );
}
